import { useEffect, useRef, useState, type ReactNode } from "react";
import { FolderTree, Loader2, Settings, Shield, Trash2, Unplug, Upload, UserRound, X } from "lucide-react";
import { AccountAvatar } from "./AccountAvatar";
import { AccountIconPicker } from "./AccountIconPicker";
import {
  normalizeAccountSettingsFocus,
  type AccountSettingsFocus,
  type AccountSettingsTab,
} from "./accountSettingsFocus";
import { AccountSettingsPasswordForm } from "./AccountSettingsPasswordForm";
import { MailStorageSettingsPanel } from "./MailStorageSettingsPanel";
import { MailFolderLayoutEditor } from "./MailFolderLayoutEditor";
import { SignatureHtmlEditor } from "./SignatureHtmlEditor";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { saveMailAccountSettings } from "@/lib/api/accountSettings";
import { setDefaultMailAccount } from "@/lib/api/accounts";
import { looksLikeHtml, normalizeStoredSignatureHtml, signatureToHtml } from "@/lib/compose/composeHtml";
import {
  readAccountSettings,
  writeAccountSettings,
  type MailAccountSettings,
} from "@/lib/mail/accountSettingsPrefs";
import { getAccountDisplayLabel } from "@/lib/mail/accountDisplayLabel";
import { isGoogleOAuthMailAccount } from "@/lib/accounts/googleOAuthAccounts";
import { isMicrosoftOAuthMailAccount } from "@/lib/accounts/microsoftOAuthAccounts";
import { fetchMailProviders } from "@/lib/api/accounts";
import { deleteMailAccountPhoto, uploadMailAccountPhoto } from "@/lib/api/accountPhotos";
import { invalidateAccountPhotoCache } from "@/lib/mail/accountPhotoCache";
import { cn } from "@/lib/cn";
import type { MailAccountSummary, MailFolder } from "@/lib/types/mail";

type Props = {
  open: boolean;
  account: MailAccountSummary | null;
  folders?: MailFolder[];
  focusSection?: AccountSettingsFocus;
  disconnectBusy?: boolean;
  onClose: () => void;
  onSaved: (accountId: number, settings: MailAccountSettings) => void;
  onAccountUpdated?: (accountId: number, patch: Partial<MailAccountSummary>) => void;
  onDefaultChanged?: (accountId: number) => void;
  onDisconnect: (accountId: number) => void;
  onPasswordUpdated: () => void;
  onPhotoUpdated?: () => void;
  onCachePurged?: () => void;
  /** force=true → LIST IMAP ; sinon cache / soft (défaut). Peut être async. */
  onCacheRefreshed?: (options?: { force?: boolean }) => void | Promise<void>;
};

function SettingsSection({
  title,
  children,
  hint,
}: {
  title: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <section className="space-y-3 rounded-none border border-[var(--mail-border-soft)] bg-[var(--mail-surface)]/60 px-4 py-4 sm:px-5">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold text-[var(--mail-text)]">{title}</h3>
        {hint ? <p className="text-xs text-[var(--mail-muted)]">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

const TABS: Array<{
  id: AccountSettingsTab;
  label: string;
  short: string;
  icon: typeof FolderTree;
}> = [
  { id: "folders", label: "Dossiers IMAP", short: "Dossiers", icon: FolderTree },
  { id: "identity", label: "Identité", short: "Identité", icon: UserRound },
  { id: "connection", label: "Connexion & données", short: "Connexion", icon: Shield },
];

export function MailAccountSettingsModal({
  open,
  account,
  folders = [],
  focusSection = "folders",
  disconnectBusy,
  onClose,
  onSaved,
  onAccountUpdated,
  onDefaultChanged,
  onDisconnect,
  onPasswordUpdated,
  onPhotoUpdated,
  onCachePurged,
  onCacheRefreshed,
}: Props) {
  const [draft, setDraft] = useState<MailAccountSettings>(() =>
    account ? readAccountSettings(account.id) : readAccountSettings(0)
  );
  const [displayNameDraft, setDisplayNameDraft] = useState("");
  const [providerLabel, setProviderLabel] = useState<string>("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoRemoving, setPhotoRemoving] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AccountSettingsTab>(() =>
    normalizeAccountSettingsFocus(focusSection)
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [defaultBusy, setDefaultBusy] = useState(false);
  const [foldersRefreshing, setFoldersRefreshing] = useState(false);
  const [storagePanelReady, setStoragePanelReady] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !account) return;
    const settings = readAccountSettings(account.id);
    const signature = settings.signature.trim();
    setDraft({
      ...settings,
      signature:
        !signature || looksLikeHtml(signature) ? settings.signature : signatureToHtml(signature),
    });
    setDisplayNameDraft(account.displayName?.trim() ?? "");
    setPhotoError(null);
    setSaveError(null);
    setActiveTab(normalizeAccountSettingsFocus(focusSection));
    let cancelled = false;
    void fetchMailProviders().then((res) => {
      if (cancelled || !res.ok || !account) return;
      const match = res.data.providers.find((p) => p.id === account.providerId);
      setProviderLabel(match?.label ?? account.providerId);
    });
    return () => {
      cancelled = true;
    };
  }, [open, account, focusSection]);

  useEffect(() => {
    if (!open) {
      setStoragePanelReady(false);
      return;
    }
    const timer = window.setTimeout(() => setStoragePanelReady(true), 450);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    contentRef.current?.scrollTo({ top: 0 });
  }, [open, activeTab]);

  if (!account) return null;

  const previewAccount = {
    email: account.email,
    displayName: displayNameDraft.trim() || account.displayName,
  };
  const headerLabel = getAccountDisplayLabel(previewAccount);
  const hasCustomLabel = headerLabel !== account.email;

  async function handleSave() {
    const signatureHtml = normalizeStoredSignatureHtml(
      looksLikeHtml(draft.signature) || !draft.signature.trim()
        ? draft.signature
        : signatureToHtml(draft.signature)
    );
    const normalizedDisplayName = displayNameDraft.trim() || null;
    const saved = writeAccountSettings(account!.id, { ...draft, signature: signatureHtml });

    setSaving(true);
    setSaveError(null);
    try {
      const res = await saveMailAccountSettings(account!.id, {
        signature: signatureHtml,
        displayName: normalizedDisplayName,
      });
      if (!res.ok) {
        setSaveError(res.message);
        return;
      }
      onAccountUpdated?.(account!.id, {
        displayName: normalizedDisplayName ?? undefined,
      });
      onSaved(account!.id, saved);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function handleSetDefault() {
    if (!account || account.isDefault || defaultBusy) return;
    setDefaultBusy(true);
    setSaveError(null);
    const res = await setDefaultMailAccount(account.id);
    setDefaultBusy(false);
    if (!res.ok) {
      setSaveError(res.message);
      return;
    }
    onAccountUpdated?.(account.id, { isDefault: true });
    onDefaultChanged?.(account.id);
  }

  function handleDisconnect() {
    if (!account) return;
    const ok = window.confirm(
      `Déconnecter la boîte ${account.email} ? Vous pourrez la reconnecter ensuite.`
    );
    if (!ok) return;
    onDisconnect(account.id);
  }

  async function handlePhotoChange(file: File | null) {
    if (!account || !file) return;
    setPhotoUploading(true);
    setPhotoError(null);
    const res = await uploadMailAccountPhoto(account.id, file);
    setPhotoUploading(false);
    if (!res.ok) {
      setPhotoError(res.message);
      return;
    }
    invalidateAccountPhotoCache(account.id);
    onAccountUpdated?.(account.id, {
      photoVersion: res.data.photoVersion,
      photoUrl: res.data.photoUrl || null,
    });
    onPhotoUpdated?.();
  }

  async function handlePhotoRemove() {
    if (!account || (account.photoVersion ?? 0) <= 0) return;
    setPhotoRemoving(true);
    setPhotoError(null);
    const res = await deleteMailAccountPhoto(account.id);
    setPhotoRemoving(false);
    if (!res.ok) {
      setPhotoError(res.message);
      return;
    }
    invalidateAccountPhotoCache(account.id);
    onAccountUpdated?.(account.id, { photoVersion: 0, photoUrl: null });
    onPhotoUpdated?.();
  }

  const photoBusy = photoUploading || photoRemoving;
  const hasPhoto = (account.photoVersion ?? 0) > 0 && Boolean(account.photoUrl);

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      animation="fade"
      variant="full"
      labelledBy="account-settings-title"
      panelClassName="flex flex-col rounded-none bg-[var(--mail-bg)]"
    >
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--mail-border-soft)] px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--mail-accent-soft)]">
            <Settings className="h-4 w-4 text-[var(--mail-accent)]" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id="account-settings-title" className="text-base font-semibold text-[var(--mail-text)]">
              Paramètres du compte
            </h2>
            <p className="truncate text-sm font-medium text-[var(--mail-text)]">{headerLabel}</p>
            {hasCustomLabel ? (
              <p className="truncate text-xs text-[var(--mail-muted)]">{account.email}</p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)]"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <nav
        className="flex shrink-0 gap-1 overflow-x-auto border-b border-[var(--mail-border-soft)] px-3 py-2 sm:px-6"
        aria-label="Sections des paramètres"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "inline-flex shrink-0 items-center gap-2 rounded-none px-3 py-2 text-xs font-semibold transition sm:px-4 sm:text-sm",
                selected
                  ? "bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]"
                  : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
              )}
              aria-current={selected ? "page" : undefined}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="sm:hidden">{tab.short}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <div
        ref={contentRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5"
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
          {activeTab === "folders" ? (
            <>
              <SettingsSection
                title="Organisation des dossiers"
                hint="Associez chaque rôle à un vrai dossier IMAP. Les pastilles indiquent si le branchement est OK."
              >
                <MailFolderLayoutEditor
                  accountId={account.id}
                  folders={folders}
                  draft={draft}
                  onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
                  onFolderCreated={() => onCacheRefreshed?.({ force: true })}
                  onRefreshFolders={() => {
                    setFoldersRefreshing(true);
                    setSaveError(null);
                    void Promise.resolve(onCacheRefreshed?.({ force: true }))
                      .catch((err: unknown) => {
                        setSaveError(
                          err instanceof Error
                            ? err.message
                            : "Impossible de rafraîchir les dossiers IMAP (délai / Gmail)."
                        );
                      })
                      .finally(() => setFoldersRefreshing(false));
                  }}
                  foldersRefreshing={foldersRefreshing}
                />
                <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg px-1 py-2 transition hover:bg-[var(--mail-surface-hover)]">
                  <input
                    type="checkbox"
                    checked={draft.showExtraFolders}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, showExtraFolders: e.target.checked }))
                    }
                    className="h-4 w-4 rounded border-[var(--mail-border-soft)] accent-[var(--mail-accent)]"
                  />
                  <span className="text-sm">Afficher « Tous les dossiers »</span>
                </label>
              </SettingsSection>
            </>
          ) : null}

          {activeTab === "identity" ? (
            <>
              <SettingsSection
                title="Profil"
                hint="Apparence du compte dans la navigation et les envois."
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <AccountAvatar account={account} size="lg" iconKeyOverride={draft.accountIcon} />
                    <div className="min-w-0 text-xs">
                      <p className="font-medium text-[var(--mail-text)]">
                        {providerLabel || account.providerId}
                      </p>
                      <p className="mt-0.5 text-[var(--mail-muted)]">
                        IMAP {account.imapHost} · SMTP {account.smtpHost}
                      </p>
                      {account.isDefault ? (
                        <p className="mt-1 text-[10px] font-medium text-[var(--mail-accent)]">
                          Compte par défaut
                        </p>
                      ) : (
                        <button
                          type="button"
                          disabled={defaultBusy || saving}
                          onClick={() => void handleSetDefault()}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-[var(--mail-border-soft)] px-2.5 py-1.5 text-[10px] font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
                        >
                          {defaultBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          ) : null}
                          Définir comme compte par défaut
                        </button>
                      )}
                    </div>
                  </div>

                  <AccountIconPicker
                    providerId={account.providerId}
                    value={draft.accountIcon}
                    onChange={(accountIcon) => setDraft((prev) => ({ ...prev, accountIcon }))}
                    disabled={photoBusy || saving}
                  />

                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[var(--mail-border-soft)] px-3 py-3 text-sm transition",
                      photoUploading
                        ? "cursor-wait bg-[var(--mail-surface-hover)] text-[var(--mail-muted)]"
                        : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]",
                      photoBusy && !photoUploading && "pointer-events-none opacity-50"
                    )}
                  >
                    {photoUploading ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--mail-accent)]" />
                    ) : (
                      <Upload className="h-4 w-4 shrink-0" />
                    )}
                    <span>
                      {photoUploading
                        ? "Envoi de l’icône…"
                        : "Icône personnalisée (photo, PNG, JPG, WebP…)"}
                    </span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="sr-only"
                      disabled={photoBusy}
                      onChange={(e) => void handlePhotoChange(e.target.files?.[0] ?? null)}
                    />
                  </label>

                  {hasPhoto ? (
                    <button
                      type="button"
                      disabled={photoBusy}
                      onClick={() => void handlePhotoRemove()}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--mail-border-soft)] px-3 py-2 text-xs font-medium text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)] disabled:opacity-50"
                    >
                      {photoRemoving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      )}
                      Retirer l&apos;icône personnalisée
                    </button>
                  ) : null}

                  {photoError ? (
                    <p className="text-xs text-red-600" role="alert">
                      {photoError}
                    </p>
                  ) : null}
                </div>
              </SettingsSection>

              <SettingsSection
                title="Nom du compte"
                hint="Affiché dans la barre latérale. Laissez vide pour l’adresse e-mail."
              >
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[var(--mail-text)]">Nom affiché</span>
                  <input
                    type="text"
                    value={displayNameDraft}
                    onChange={(e) => setDisplayNameDraft(e.target.value)}
                    placeholder={account.email}
                    maxLength={80}
                    className="w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] outline-none transition focus:border-[var(--mail-accent)] focus:ring-2 focus:ring-[var(--mail-accent)]/20"
                  />
                </label>
                <p className="text-xs text-[var(--mail-muted)]">
                  Adresse :{" "}
                  <span className="font-medium text-[var(--mail-text)]">{account.email}</span>
                </p>
              </SettingsSection>

              <SettingsSection
                title="Signature"
                hint="Propre à ce compte — HTML accepté (tableaux, logos, liens)."
              >
                <SignatureHtmlEditor
                  value={draft.signature}
                  onChange={(html) => setDraft((prev) => ({ ...prev, signature: html }))}
                  placeholder="&lt;table&gt;…&lt;/table&gt; ou texte simple"
                />
              </SettingsSection>
            </>
          ) : null}

          {activeTab === "connection" ? (
            <>
              <SettingsSection title="Connexion" hint="Identifiants serveur pour ce compte.">
                {isGoogleOAuthMailAccount(account) || isMicrosoftOAuthMailAccount(account) ? (
                  <div className="space-y-3 rounded-none border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    <p>
                      Ce compte OAuth est suspendu dans Aurélia Mail (connexion désactivée).
                      Il ne synchronise plus pour éviter de ralentir l&apos;application.
                    </p>
                    <p className="text-xs text-amber-900/80">
                      Utilisez « Déconnecter ce compte » ci-dessous pour le retirer, puis
                      reconnectez-le avec un mot de passe d&apos;application Gmail si besoin.
                    </p>
                  </div>
                ) : (
                  <AccountSettingsPasswordForm account={account} onSuccess={onPasswordUpdated} />
                )}
              </SettingsSection>

              <MailStorageSettingsPanel
                accountId={account.id}
                folders={folders}
                active={open && storagePanelReady && activeTab === "connection"}
                sectionPadding="compact"
                onPurged={onCachePurged}
              />

              <SettingsSection title="Compte">
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={disconnectBusy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-none border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                >
                  <Unplug className="h-4 w-4 shrink-0" aria-hidden />
                  {disconnectBusy ? "Déconnexion…" : "Déconnecter ce compte"}
                </button>
              </SettingsSection>
            </>
          ) : null}
        </div>
      </div>

      <footer className="flex shrink-0 flex-col items-stretch gap-2 border-t border-[var(--mail-border-soft)] bg-[var(--mail-surface)]/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-6">
        {saveError ? (
          <p className="w-full text-xs text-red-600 sm:mr-auto sm:w-auto" role="alert">
            {saveError}
          </p>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-full px-4 py-2.5 text-sm text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || defaultBusy}
          className="mail-btn-primary inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Enregistrer
        </button>
      </footer>
    </MailModalOverlay>
  );
}
